import { Market1X2Item } from '@/models/market1X2';
import { MarketGoalItem } from '@/models/marketGoal';
import { MarketDcItem } from '@/models/marketDc';
import { MarketHandicapItem } from '@/models/marketHandicap';
import { MarketCsItem } from '@/models/marketCs';
import { MarketCornersGoalItem } from '@/models/marketCornersGoal';
import { MarketCornersHandicapItem } from '@/models/marketCornersHandicap';
import { MarketBtsItem } from '@/models/marketBts';
import { EventStatsItem } from '@/models/eventStats';
import { EventFeedType, Jollywhere, LeagueLimitsCategory, MarketGroup } from '@/constants';
import { useLeagueFavorite } from '@/composables/useLeagueFavorite';
import { useTeamFavorite } from '@/composables/useTeamFavorite';
import { FeedLiveItem } from '@/models/feedLive';
import { FeedPregameItem } from '@/models/feedPregame';
import { useAppStore } from '@/stores/app';
import equal from 'fast-deep-equal';
import isEmpty from 'lodash-es/isEmpty';
import { BetItem } from '@/models/bet';

export const useEventStore = defineStore('event', () => {
    const appStore = useAppStore();

    const isDialogOpen = ref<boolean>(false);

    const customEventId = ref<string>();
    const eventFeedType = ref<EventFeedType>();

    const iso3 = ref<string>();
    const leagueId = ref<number>();
    const leagueName = ref<string>();
    const jollywhere = ref<Jollywhere | null>(null);
    const leagueLimitsCategoryId = ref<LeagueLimitsCategory>();

    const teamHomeId = ref<number>();
    const teamHomeName = ref<string>();
    const teamHomeLogo = ref<string | null>(null);

    const teamAwayId = ref<number>();
    const teamAwayName = ref<string>();
    const teamAwayLogo = ref<string | null>(null);

    const eventStartedAt = ref<string>();
    const leftUntilStart = ref<number>();

    const stats = ref<EventStatsItem | null>(null);

    const favoriteEvent = ref<Record<string, number>>({});
    const favoriteEventProcessing = reactive<Record<string, boolean>>({});

    const marketGoalFT = ref<MarketGoalItem[] | null>(null);
    const marketHandicapFT = ref<MarketHandicapItem[] | null>(null);
    const marketCornersGoalFT = ref<MarketCornersGoalItem[] | null>(null);
    const marketCornersHandicapFT = ref<MarketCornersHandicapItem[] | null>(null);
    const market1x2FT = ref<Market1X2Item | null>(null);
    const marketBtsFT = ref<MarketBtsItem | null>(null);
    const marketDcFT = ref<MarketDcItem | null>(null);
    const marketCsFT = ref<MarketCsItem[] | null>(null);
    const marketGoal1H = ref<MarketGoalItem[] | null>(null);
    const marketHandicap1H = ref<MarketHandicapItem[] | null>(null);
    const marketCornersGoal1H = ref<MarketCornersGoalItem[] | null>(null);
    const marketCornersHandicap1H = ref<MarketCornersHandicapItem[] | null>(null);
    const market1x21H = ref<Market1X2Item | null>(null);
    const marketBts1H = ref<MarketBtsItem | null>(null);
    const marketDc1H = ref<MarketDcItem | null>(null);
    const marketCs1H = ref<MarketCsItem[] | null>(null);

    function setFavoriteEvents(events: Record<string, number>) {
        if (!equal(favoriteEvent.value, events)) {
            console.log('Set favorite events');
            favoriteEvent.value = events;
        }
    }

    function resetMarketsFT() {
        marketGoalFT.value = null;
        marketHandicapFT.value = null;
        marketCornersGoalFT.value = null;
        marketCornersHandicapFT.value = null;
        market1x2FT.value = null;
        marketBtsFT.value = null;
        marketDcFT.value = null;
        marketCsFT.value = null;
    }

    function resetMarkets1H() {
        marketGoal1H.value = null;
        marketHandicap1H.value = null;
        marketCornersGoal1H.value = null;
        marketCornersHandicap1H.value = null;
        market1x21H.value = null;
        marketBts1H.value = null;
        marketDc1H.value = null;
        marketCs1H.value = null;
    }

    function resetMarkets() {
        resetMarketsFT();
        resetMarkets1H();
    }

    function resetEventData() {
        isDialogOpen.value = false;
        customEventId.value = undefined;
        eventFeedType.value = undefined;
        iso3.value = undefined;
        leagueId.value = undefined;
        leagueName.value = undefined;
        teamHomeId.value = undefined;
        teamHomeName.value = undefined;
        teamHomeLogo.value = null;
        teamAwayId.value = undefined;
        teamAwayName.value = undefined;
        teamAwayLogo.value = null;
        eventStartedAt.value = undefined;
        leftUntilStart.value = undefined;
        stats.value = null;
        jollywhere.value = null;

        resetMarkets();
    }

    function isLiveStatsExist(): boolean {
        return eventFeedType.value === EventFeedType.Live && stats.value!.isStatsExist();
    }

    function openEventDialog(feedLine: FeedLiveItem | FeedPregameItem) {
        customEventId.value = feedLine.customEventId.value;
        eventFeedType.value = feedLine.eventFeedType.value!;

        iso3.value = feedLine.iso3.value!;
        leagueId.value = feedLine.leagueId.value;
        leagueName.value = feedLine.leagueName.value;
        leagueLimitsCategoryId.value = feedLine.limitsCategoryId.value;

        teamHomeId.value = feedLine.teamHomeId.value;
        teamHomeName.value = feedLine.teamHomeName.value;
        teamHomeLogo.value = feedLine.teamHomeLogo.value;

        teamAwayId.value = feedLine.teamAwayId.value;
        teamAwayName.value = feedLine.teamAwayName.value;
        teamAwayLogo.value = feedLine.teamAwayLogo.value;

        jollywhere.value = feedLine.jollywhere.value;

        eventStartedAt.value = feedLine.eventStartedAt.value!;

        //need to init event stats to avoid data jumping after stats loading
        if (feedLine instanceof FeedLiveItem) {
            stats.value = new EventStatsItem({
                score: feedLine.scoreFull,
                scores: JSON.stringify(feedLine.scoreList.value),
                is_scores_mismatch: feedLine.isScoreMismatch.value,
                time: feedLine.time.value ?? null,
                times: JSON.stringify(feedLine.timeList),
                red_home: feedLine.redHome.value,
                red_away: feedLine.redAway.value,
                yellow_home: feedLine.yellowHome.value,
                yellow_away: feedLine.yellowAway.value,
                fouls_home: feedLine.foulsHome.value,
                fouls_away: feedLine.foulsAway.value,
                possession_time_home: feedLine.possessionTimeHome.value,
                possession_time_away: feedLine.possessionTimeAway.value,
                attacks_home: feedLine.attacksHome.value,
                attacks_away: feedLine.attacksAway.value,
                dangerous_attacks_home: feedLine.dangerousAttacksHome.value,
                dangerous_attacks_away: feedLine.dangerousAttacksAway.value,
                shots_home: feedLine.shotsHome.value,
                shots_away: feedLine.shotsAway.value,
                shots_on_target_home: feedLine.shotsOnTargetHome.value,
                shots_on_target_away: feedLine.shotsOnTargetAway.value,
                hit_woodwork_home: feedLine.hitWoodworkHome.value,
                hit_woodwork_away: feedLine.hitWoodworkAway.value,
                corners_home: feedLine.cornersHome.value,
                corners_away: feedLine.cornersAway.value,
            });
        } else {
            leftUntilStart.value = feedLine.leftUntilStart.value;
        }

        isDialogOpen.value = true;
    }

    function updateMultiLineMarket(refVar: any, marketData: any[], MarketClass: any) {
        if (marketData.length === 0) {
            refVar.value = null;
        } else {
            if (refVar.value === null) {
                refVar.value = marketData.map((item: any) => new MarketClass(item));
            } else {
                marketData.forEach((item: any, idx: number) => {
                    if (refVar.value && refVar.value[idx]) {
                        refVar.value[idx].update(item);
                    } else if (refVar.value) {
                        refVar.value.push(new MarketClass(item));
                    }
                });
                refVar.value.splice(marketData.length);
            }
        }
    }

    /**
     * Helper to manage single‑line markets (non‑array objects).
     * @param targetRef  The `ref` holding a market item (or null).
     * @param data       Fresh data coming from WS (object or array).
     * @param ItemClass  Class used to construct the market item.
     */
    function updateSingleLineMarket<T>(targetRef: Ref<T | null>, data: any, ItemClass: { new (data: any): T }) {
        if (Array.isArray(data)) {
            targetRef.value = null;
            return;
        }

        if (targetRef.value === null) {
            targetRef.value = new ItemClass(data);
        } else {
            (targetRef.value as any).update(data);
        }
    }

    function updateEventPopup(data: any) {
        //check if Event popup still be opened. We can receive update after popup closing
        if (customEventId.value === undefined) {
            return;
        }
        //empty data when event is finished
        if (!data) {
            return;
        }

        if (data['custom_event_id'] !== customEventId.value) {
            console.log('Received wrong Custom Event Id. Received #' + data['custom_event_id'] + ' but current is #' + customEventId.value);
            return;
        }

        let base, statsData;
        if ('ft' in data) {
            base = data['ft'].base;
            statsData = data['ft'].stats;

            // const data1X2 = data['ft'].market1x2;
            // //todo for testing
            // data1X2['price_home_max'] += Math.random() * 0.04 - 0.02;
            // data1X2['price_draw_max'] += Math.random() * 0.04 - 0.02;
            // data1X2['price_away_max'] += Math.random() * 0.04 - 0.02;

            updateSingleLineMarket(market1x2FT, data['ft'].market1x2, Market1X2Item);
            updateSingleLineMarket(marketBtsFT, data['ft'].marketBtts, MarketBtsItem);
            updateSingleLineMarket(marketDcFT, data['ft'].marketDc, MarketDcItem);

            updateMultiLineMarket(marketCsFT, data['ft'].marketCs, MarketCsItem);
            updateMultiLineMarket(marketGoalFT, data['ft'].marketGoal, MarketGoalItem);
            updateMultiLineMarket(marketHandicapFT, data['ft'].marketHandicap, MarketHandicapItem);
            updateMultiLineMarket(marketCornersGoalFT, data['ft'].marketCornersGoal, MarketCornersGoalItem);
            updateMultiLineMarket(marketCornersHandicapFT, data['ft'].marketCornersHandicap, MarketCornersHandicapItem);
        } else {
            resetMarketsFT();
        }

        if ('1h' in data) {
            base = base ?? data['1h'].base;
            statsData = statsData ?? data['1h'].stats;

            updateSingleLineMarket(market1x21H, data['1h'].market1x2, Market1X2Item);
            updateSingleLineMarket(marketBts1H, data['1h'].marketBtts, MarketBtsItem);
            updateSingleLineMarket(marketDc1H, data['1h'].marketDc, MarketDcItem);

            updateMultiLineMarket(marketCs1H, data['1h'].marketCs, MarketCsItem);
            updateMultiLineMarket(marketGoal1H, data['1h'].marketGoal, MarketGoalItem);
            updateMultiLineMarket(marketHandicap1H, data['1h'].marketHandicap, MarketHandicapItem);
            updateMultiLineMarket(marketCornersGoal1H, data['1h'].marketCornersGoal, MarketCornersGoalItem);
            updateMultiLineMarket(marketCornersHandicap1H, data['1h'].marketCornersHandicap, MarketCornersHandicapItem);
        } else {
            resetMarkets1H();
        }

        if (base) {
            leftUntilStart.value = base.leftUntilStart;
            eventFeedType.value = base.source_id;
        }

        if (statsData) {
            updateSingleLineMarket(stats, statsData, EventStatsItem);
        }

        updateBetDialog();
    }

    function updateBetDialog() {
        //if was opened bet popup need to update data
        if (!isEmpty(appStore.betFeedData)) {
            for (let index in appStore.betFeedData) {
                let betFeedData = appStore.betFeedData[index];

                if (betFeedData.isCreatedFromFeed) {
                    //need to skip bettingData created from Feed pages
                    continue;
                }

                if (betFeedData.customEventId !== customEventId.value) {
                    console.log(`CustomEventId in event ${customEventId.value} and betPopup ${betFeedData.customEventId} do not match`);
                    continue;
                }

                betFeedData.eventStartedAt = eventStartedAt.value!;
                betFeedData.eventFeedType = eventFeedType.value!;

                if (stats.value) {
                    betFeedData.scoreHome = stats.value.scoreHome;
                    betFeedData.scoreAway = stats.value.scoreAway;

                    betFeedData.cornersHome = stats.value.cornersHome;
                    betFeedData.cornersAway = stats.value.cornersAway;

                    betFeedData.time = stats.value.time;
                }

                betFeedData.updatePriceDataFromEvent();

                betFeedData.isActive = betFeedData.price !== null;

                appStore.betFeedData[index] = betFeedData;
            }
        }
    }

    function getEventScoreForBet(bet: BetItem): { home: number | null; away: number | null } {
        if (eventFeedType.value !== EventFeedType.Live) {
            return { home: 0, away: 0 };
        }

        if (stats === null) {
            return { home: null, away: null };
        }
        if (bet.marketGroup === MarketGroup.Corners) {
            if ((stats.value!.time !== null && stats.value!.time.includes('1H')) || bet.isFT()) {
                return { home: stats.value!.cornersHome, away: stats.value!.cornersAway };
            } else {
                //1h bet and (2nd half or unknown time)
                return { home: bet.cornersHomeIrHt, away: bet.cornersAwayIrHt };
            }
        } else {
            if ((stats.value!.time !== null && stats.value!.time.includes('1H')) || bet.isFT()) {
                return { home: stats.value!.scoreHome, away: stats.value!.scoreAway };
            } else {
                //1h bet and (2nd half or unknown time)
                return { home: bet.scoreHomeIrHt, away: bet.scoreAwayIrHt };
            }
        }
    }

    const { isLeagueProcessing, isLeagueFavorite, toggleLeagueFavorite } = useLeagueFavorite();
    const { isTeamProcessing, isTeamFavorite, toggleTeamFavorite } = useTeamFavorite();

    return {
        customEventId,
        eventFeedType,
        iso3,
        leagueId,
        leagueName,
        leagueLimitsCategoryId,
        teamHomeId,
        teamHomeName,
        teamHomeLogo,
        teamAwayId,
        teamAwayName,
        teamAwayLogo,
        eventStartedAt,
        leftUntilStart,
        stats,
        favoriteEvent,
        favoriteEventProcessing,
        marketGoalFT,
        marketHandicapFT,
        marketCornersGoalFT,
        marketCornersHandicapFT,
        market1x2FT,
        marketBtsFT,
        marketDcFT,
        marketCsFT,
        marketGoal1H,
        marketHandicap1H,
        marketCornersGoal1H,
        marketCornersHandicap1H,
        market1x21H,
        marketBts1H,
        marketDc1H,
        marketCs1H,
        resetEventData,
        updateEventPopup,
        isLeagueProcessing,
        isLeagueFavorite,
        toggleLeagueFavorite,
        isTeamProcessing,
        isTeamFavorite,
        toggleTeamFavorite,
        isDialogOpen,
        openEventDialog,
        isLiveStatsExist,
        setFavoriteEvents,
        jollywhere,
        getEventScoreForBet,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useEventStore, import.meta.hot));
}
